import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSave,
  faBox,
  faAlignLeft,


  faTags,
  faBarcode,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import productCatalogueService from '../../services/productCatalogueService';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '../common/SearchableSelect';
import { extractApiError } from '../../utils/apiError';

/**
 * ProductCatalogueForm Component
 * Modal form for adding/editing products with validation
 */
const ProductCatalogueForm = ({
  isOpen,
  onClose,
  onSubmit,
  product = null,
  loading = false,
}) => {
  const { t } = useTranslation();
  // String constants for UI text
  const strings = t('productCatalogue.forms', { returnObjects: true });
  const requiredIndicator = t('common.symbols.required');

  const [formData, setFormData] = useState({
    product_name: '',
    description: '',


    product_category_id: '',
    hsn_code_id: ''
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [categories, setCategories] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Static cache to persist data across component instances
  // Use a module-level cache to persist across all form instances
  // Updated to fix cacheRef reference error
  if (!window.productCatalogueFormCache) {
    window.productCatalogueFormCache = {
      categories: null,
      hsnCodes: null,
      categoriesLoaded: false,
      hsnCodesLoaded: false,
      loading: false
    };
  }
  const cache = window.productCatalogueFormCache;

  // Initialize form data when product prop changes
  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name || '',
        description: product.description || '',


        product_category_id: product.product_category_id ? product.product_category_id.toString() : '',
        hsn_code_id: product.hsn_code_id ? product.hsn_code_id.toString() : ''
      });
    } else {
      setFormData({
        product_name: '',
        description: '',


        product_category_id: '',
        hsn_code_id: ''
      });
    }
    setErrors({});
    setSubmitError('');
  }, [product, isOpen]);

  const loadDropdownOptions = useCallback(async () => {
    // Check if we already have cached data - avoid API calls if data exists
    if (cache.categoriesLoaded && cache.hsnCodesLoaded) {
      if (categories.length === 0 && cache.categories) {
        setCategories(cache.categories);
      }
      if (hsnCodes.length === 0 && cache.hsnCodes) {
        setHsnCodes(cache.hsnCodes);
      }
      return;
    }

    // Prevent multiple simultaneous calls
    if (cache.loading) {
      return;
    }

    cache.loading = true;
    setLoadingOptions(true);

    try {
      const needCategories = !cache.categoriesLoaded;
      const needHsnCodes = !cache.hsnCodesLoaded;

      const promises = [];

      if (needCategories) {
        promises.push(productCatalogueService.getProductCategories());
      } else {
        promises.push(Promise.resolve({ success: true, data: cache.categories }));
      }

      if (needHsnCodes) {
        promises.push(productCatalogueService.getHsnCodes());
      } else {
        promises.push(Promise.resolve({ success: true, data: cache.hsnCodes }));
      }

      const [categoriesResult, hsnCodesResult] = await Promise.all(promises);

      if (categoriesResult && categoriesResult.success) {
        const categoriesData = categoriesResult.data;
        setCategories(categoriesData);
        // Cache the data for future use
        cache.categories = categoriesData;
        cache.categoriesLoaded = true;
      }

      if (hsnCodesResult && hsnCodesResult.success) {
        const hsnCodesData = hsnCodesResult.data;
        setHsnCodes(hsnCodesData);
        // Cache the data for future use
        cache.hsnCodes = hsnCodesData;
        cache.hsnCodesLoaded = true;
      }
    } catch (error) {
      console.error(strings.validation.dropdownError, error);
    } finally {
      cache.loading = false;
      setLoadingOptions(false);
    }
  }, [strings.validation.dropdownError, categories.length, hsnCodes.length, cache]);

  // Load dropdown options when form opens, but use cached data when available
  useEffect(() => {
    if (isOpen) {
      // First check if we have cached data and populate local state
      if (cache.categories && categories.length === 0) {
        setCategories(cache.categories);
      }
      if (cache.hsnCodes && hsnCodes.length === 0) {
        setHsnCodes(cache.hsnCodes);
      }

      // Only load if we don't have cached data and not already loading
      if ((!cache.categoriesLoaded || !cache.hsnCodesLoaded) && !cache.loading) {
        loadDropdownOptions();
      }
    }
  }, [
    isOpen,
    loadDropdownOptions,
    cache.categories,
    cache.categoriesLoaded,
    cache.hsnCodes,
    cache.hsnCodesLoaded,
    cache.loading,
    categories.length,
    hsnCodes.length,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;












    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    if (submitError) setSubmitError('');

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.product_name.trim()) {
      newErrors.product_name = strings.fields.productName.errors.required;
    } else if (formData.product_name.trim().length < 2) {
      newErrors.product_name = strings.fields.productName.errors.minLength;
    } else if (formData.product_name.trim().length > 100) {
      newErrors.product_name = strings.fields.productName.errors.maxLength;
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = strings.fields.description.errors.maxLength;
    }














    if (!formData.product_category_id) {
      newErrors.product_category_id = strings.fields.productCategoryId.errors.required;
    }

    if (!formData.hsn_code_id) {
      newErrors.hsn_code_id = strings.fields.hsnCodeId.errors.required;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const payload = {
        product_name: formData.product_name.trim(),
        description: formData.description.trim() || undefined,


        product_category_id: parseInt(formData.product_category_id),
        hsn_code_id: parseInt(formData.hsn_code_id)
      };

      setSubmitError('');
      try {
        const result = await onSubmit(payload);
        // Only close on success; keep the form open with data on failure.
        if (result && result.success === false) {
          const msg = typeof result.error === 'string' ? result.error : extractApiError(result.error);
          setSubmitError(msg || 'Failed to save product. Please check the details and try again.');
          return;
        }
        onClose();
      } catch (err) {
        setSubmitError(extractApiError(err) || 'Failed to save product. Please check the details and try again.');
      }
    }
  };

  const submitLabel = product
    ? strings.buttons.submit.edit
    : strings.buttons.submit.add;
  const submitLoadingLabel = product
    ? strings.buttons.submit.loading.edit
    : strings.buttons.submit.loading.add;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.product-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto product-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >

        <div
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <h2
            className="text-2xl font-bold"
            style={{ color: UI_COLORS.TEXT.PRIMARY }}
          >
            {product ? strings.title.edit : strings.title.add}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>


        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faBox} className="mr-2" />
              {`${strings.fields.productName.label} ${requiredIndicator}`}
            </label>
            <input
              type="text"
              name="product_name"
              value={formData.product_name}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.product_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.product_name && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields.productName.placeholder}
            />
            {errors.product_name && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faAlignLeft} className="mr-2" />
              {strings.fields.description.label}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200 resize-none"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.description ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.description && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields.description.placeholder}
            />
            {errors.description && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">




























            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >


              </label>





















            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faTags} className="mr-2" />
                {`${strings.fields.productCategoryId.label} ${requiredIndicator}`}
              </label>
              <select
                name="product_category_id"
                value={formData.product_category_id}
                onChange={handleChange}
                disabled={loadingOptions}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.product_category_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.product_category_id && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
              >
                <option value="">{strings.fields.productCategoryId.placeholder}</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name || category.category_name}
                  </option>
                ))}
              </select>
              {errors.product_category_id && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.product_category_id}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faBarcode} className="mr-2" />
                {`${strings.fields.hsnCodeId.label} ${requiredIndicator}`}
              </label>
              <SearchableSelect
                name="hsn_code_id"
                value={formData.hsn_code_id}
                onChange={handleChange}
                disabled={loadingOptions}
                placeholder={loadingOptions ? 'Loading...' : strings.fields.hsnCodeId.placeholder}
                error={errors.hsn_code_id}
                options={hsnCodes.map(h => ({
                  value: h.id,
                  label: h.description ? `${h.code || h.hsn_code} - ${h.description}` : (h.code || h.hsn_code),
                }))}
              />
              {errors.hsn_code_id && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.hsn_code_id}
                </p>
              )}
            </div>
          </div>

          {submitError && (
            <div
              className="rounded-xl px-4 py-3 text-sm font-medium"
              role="alert"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                color: UI_COLORS.STATUS.ERROR,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
              }}
            >
              {submitError}
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                color: UI_COLORS.BUTTONS.SECONDARY.text,
                border: `2px solid ${UI_COLORS.BUTTONS.SECONDARY.border}`
              }}
            >
              {strings.buttons.cancel}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
                border: `2px solid ${UI_COLORS.BUTTONS.PRIMARY.background}`
              }}
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {loading ? submitLoadingLabel : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductCatalogueForm;